import React, { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { useBackClose } from "@/lib/backstack";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "adeshwar_assistant_session_v1";
const TOKEN_KEY = "adeshwar_customer_token_v1";

function sessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

function renderContent(text) {
  // Render markdown-style **bold** as <strong> without leaving literal asterisks.
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

export default function AssistantChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  useBackClose(open, () => setOpen(false));

  useEffect(() => {
    if (!open || messages.length) return;
    fetch(`${API}/assistant/history/${sessionId()}`)
      .then((r) => r.json())
      .then((d) => setMessages(d.messages || []))
      .catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ session_id: sessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const j = JSON.parse(data);
            const chunk = j.delta || j.error || "";
            if (chunk) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
                return copy;
              });
            }
          } catch { /* partial frame */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't reply right now. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="assistant-fab"
        onClick={() => setOpen(true)}
        aria-label="Ask AI assistant"
        title="Ask AI assistant"
        className="fixed z-40 bottom-36 right-5 sm:bottom-24 sm:right-7 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white shadow-lg shadow-indigo-500/30 grid place-items-center transition-all"
      >
        <Sparkles size={22} />
      </button>

      {open && (
        <div data-testid="assistant-panel" className="fixed z-50 inset-0 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-[400px] sm:h-[560px] bg-white sm:rounded-2xl sm:shadow-2xl sm:border sm:border-gray-200 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 h-14 bg-indigo-600 text-white shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles size={18} />
              <div>
                <div className="font-black text-sm leading-tight">Adeshwar AI Assistant</div>
                <div className="text-[11px] text-indigo-200 leading-tight">Ask about products, sizing & availability</div>
              </div>
            </div>
            <button data-testid="assistant-close-btn" onClick={() => setOpen(false)} aria-label="Close assistant" className="w-8 h-8 rounded-full hover:bg-white/15 grid place-items-center">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-xs text-gray-500 bg-white border border-gray-200 rounded-xl p-3">
                Hi! I can help you pick the right electrical products — try:
                <ul className="mt-1.5 space-y-1 font-semibold text-indigo-700">
                  <li>"Which MCB do I need for a 1.5-ton AC?"</li>
                  <li>"Wire size for a 15A power socket?"</li>
                  <li>"Show me Legrand switch options"</li>
                </ul>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`assistant-message-${m.role}`} className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-indigo-600 text-white rounded-br-sm" : "bg-white border border-gray-200 text-gray-800 rounded-bl-sm"}`}>
                {m.content ? renderContent(m.content) : (busy && i === messages.length - 1 ? <Loader2 size={15} className="animate-spin text-indigo-500" /> : "")}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="p-2.5 border-t border-gray-200 bg-white flex items-center gap-2 shrink-0">
            <input
              data-testid="assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Ask anything…"
              className="flex-1 h-11 px-3 rounded-xl border border-gray-300 focus:border-indigo-500 outline-none text-sm"
            />
            <button
              data-testid="assistant-send-btn"
              disabled={busy || !input.trim()}
              onClick={send}
              aria-label="Send message"
              className="w-11 h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white grid place-items-center"
            >
              {busy ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
